import React from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTags, faList, faFileAlt } from '@fortawesome/pro-solid-svg-icons';

import ManagedInput from 'components/form/managed_input';
import TitleBar from 'components/title_bar';
import Errors from 'components/form/errors';
import CompEntriesTable from './entries_table';
import CompReportAttributeSelector from './attribute_selector';
import * as api from './api';

export default class CompReportForm extends React.Component {
  static propTypes = {
    attributeOptions: PropTypes.array,
    availableTemplates: PropTypes.array.isRequired,
    compReport: PropTypes.shape({
      compAttributes: PropTypes.array,
      compReportEntries: PropTypes.array,
      type: PropTypes.string
    }).isRequired,
    type: PropTypes.string
  }

  state = {
    compReport: {
      ...this.props.compReport,
      compAttributes: this.selectedAttributes(this.props),
      name: '',
      docsDocumentTemplateId: this.props.availableTemplates[0].id
    },
    saving: false,
    errors: []
  }

  selectedAttributes({ compReport, attributeOptions }) {
    return compReport.compAttributes || attributeOptions.filter(option => option.default).map(option => option.key);
  }

  reorderComps = (dragIndex, hoverIndex) => {
    this.setState((state) => {
      const { compReportEntries } = state.compReport;
      const dragEntry = compReportEntries[dragIndex];

      compReportEntries.splice(dragIndex, 1);
      compReportEntries.splice(hoverIndex, 0, dragEntry);

      const compReport = { ...state.compReport, compReportEntries };

      return { compReport };
    });
  }

  reorderAttributes = (dragIndex, hoverIndex) => {
    this.setState((state) => {
      const { compAttributes } = state.compReport;
      const dragEntry = compAttributes[dragIndex];

      compAttributes.splice(dragIndex, 1);
      compAttributes.splice(hoverIndex, 0, dragEntry);

      const compReport = { ...state.compReport, compAttributes };
      return { compReport };
    });
  }

  handleClearAttributes  = () => {
    this.setState((state) => {
      const compReport = { ...state.compReport, compAttributes: [] };

      return { compReport };
    });
  }

  handleSelectAttribute = (attribute) => {
    this.setState((state) => {
      const compReport = {
        ...state.compReport,
        compAttributes: [...state.compReport.compAttributes, attribute.key]
      };

      return { compReport };
    });
  }

  handleRemoveAttribute = (attribute) => {
    this.setState((state) => {
      const compReport = {
        ...state.compReport,
        compAttributes: state.compReport.compAttributes.filter(a => a != attribute.key)
      };

      return { compReport };
    });
  }

  handleRemoveEntry = (comp) => {
    this.setState(({ compReport }) => ({
      compReport: {
        ...compReport,
        compReportEntries: compReport.compReportEntries.filter(a => a.comp.id !== comp.id)
      }
    }));
  }

  disableSave = () => {
    const { name, saving } = this.state.compReport;
    return (saving || name.length < 1);
  }

  createReportParams = () => {
    const { _attributeOptions, type, ...compReport } = this.state.compReport;
    const compIds = compReport.compReportEntries.map(e => e.comp.id);

    return {
      researchCompReport: {
        ...compReport,
        compIds,
        type
      },
      type
    };
  }

  createReport = () => {
    api.createReport(this.createReportParams())
      .then(data => window.location.assign(data.editPath));
  }

  handleChange = (changes) => {
    this.setState(prevState => ({ compReport: { ...prevState.compReport, ...changes } }));
  }

  renderAttributeSelector() {
    return (
      <CompReportAttributeSelector
        attributeOptions={this.props.attributeOptions}
        selectedAttributes={this.state.compReport.compAttributes}
        onAttributeRemove={this.handleRemoveAttribute}
        onAttributesClear={this.handleClearAttributes}
        onAttributeSelect={this.handleSelectAttribute}
        onReorder={this.reorderAttributes}
      />
    );
  }

  renderCompEntries() {
    return (
      <CompEntriesTable
        compReportEntries={this.state.compReport.compReportEntries}
        onRemoveEntry={this.handleRemoveEntry}
        onReorder={this.reorderComps}
      />
    );
  }

  renderCTA() {
    const nameFilled = this.state.compReport.name.length > 0;
    const disabled = this.disableSave();
    const button = (
      <button
        className="btn btn-save"
        disabled={disabled}
        name="submit"
        style={{ pointerEvents: disabled ? 'none' : 'all' }}
        onClick={this.createReport}
      >
        Generate Report
      </button>
    );

    return (
      <div className="top-buffer-lg">
        {nameFilled ? button : (
          <OverlayTrigger overlay={<Tooltip id="cta-tooltip">Report Name is required</Tooltip>} placement="top">
            <div style={{ cursor: 'not-allowed', width: 'min-content' }}>{button}</div>
          </OverlayTrigger>
        )}
      </div>
    );
  }

  renderInputs() {
    return (
      <React.Fragment>
        <div className="form-group">
          <label>
            Report Name
            <span className="text-danger"> *</span>
          </label>
          <ManagedInput
            className="form-control"
            name="name"
            required
            value={this.state.compReport.name}
            onChange={this.handleChange}
          />
        </div>

        <div className="form-group mb-5">
          <label>Document Layout</label>
          <ManagedInput
            className="form-control"
            name="docsDocumentTemplateId"
            type="select"
            value={this.state.compReport.docsDocumentTemplateId}
            onChange={this.handleChange}
          >
            {this.props.availableTemplates.map(({ name, id }) => <option key={id} value={id}>{name}</option>)}
          </ManagedInput>
        </div>
      </React.Fragment>
    );
  }

  render() {
    const { errors } = this.state;
    return (
      <div>
        <TitleBar
          subtitle="Create a printable Comp report for your records or to share with your clients"
          title="Create New Comp Report"
        />
        <div className="container white">
          <Errors errors={errors}/>
          <h3 className="mb-5 mt-0">
            <FontAwesomeIcon className="mr-3" icon={faFileAlt} />
            Report Settings
          </h3>
          {this.renderInputs()}
          <hr />
          <h3 className="my-5">
            <FontAwesomeIcon className="mr-3" icon={faTags} />
            Comps <br/>
            <small>Drag and drop Comps to put them in your preferred order.</small>
          </h3>
          {this.renderCompEntries()}
          <hr />
          <h3 className="my-5">
            <FontAwesomeIcon className="mr-3" icon={faList} />
            Fields <br />
            <small>Which fields would you like to show for the Comps in this report?</small>
          </h3>
          {this.renderAttributeSelector()}
          {this.renderCTA()}
        </div>
      </div>
    );
  }
}
